import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ROTAS, grupoDaRota } from '@/lib/navegacao';
import { useCommandPalette } from '@/lib/stores/commandPaletteStore';
import { useAuthStore } from '@/lib/stores/authStore';
import { useMockStore } from '@/lib/stores/mockStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { contatosApi } from '@/lib/api/contatos';
import { solicitacoesApi } from '@/lib/api/solicitacoes';
import { acoesApi } from '@/lib/api/acoes';
import { liderancasApi } from '@/lib/api/liderancas';

/**
 * Paleta de comandos — telas e entidades (proto:562-571).
 *
 * Tetos POR CATEGORIA antes do teto global de 14 (proto:563-570): só o teto
 * global faria uma busca que casa muitos contatos engolir as demais
 * categorias.
 *
 * O que NÃO está aqui, e por quê:
 *
 * - **Emendas e Agenda interna.** O protótipo busca as duas sobre arrays
 *   locais. Em produção, `GET /gab/emendas` só aceita `exercicio`
 *   (`emendas.controller.ts:33`) e `GET /gab/agenda-interna` não aceita
 *   filtro nenhum (`agenda-interna.controller.ts:50`): nenhuma das duas tem
 *   busca por texto no servidor. Baixar as duas listas inteiras a cada tecla
 *   para filtrar no cliente trocaria o custo de sete consultas por um custo
 *   pior, então as categorias ficam de fora e a lacuna é declarada na própria
 *   paleta, em vez de o placeholder prometer o que a busca não faz.
 * - **Busca de contato por id.** No protótipo o id do contato é um código
 *   humano (`CT-2700`). Aqui `contato.id` é um ULID de 26 caracteres
 *   (`contato.entity.ts:6`) que ninguém tem na mão, e a busca do servidor
 *   cobre nome, telefone e e-mail (`contatos.service.ts:97`). Quem tem um
 *   identificador na mão é quem tem o **protocolo** da solicitação — e esse
 *   sim o servidor busca (`solicitacoes.service.ts:113`).
 */

/** Espera entre a última tecla e a consulta. Sem isso seria uma rajada por tecla. */
const DEBOUNCE_MS = 250;
/** Tamanho mínimo antes de consultar o servidor. */
const MIN_BUSCA = 2;
/** Teto global do protótipo (`palItems=it.slice(0,14)`, proto:570). */
const TETO_GLOBAL = 14;
const TETO_CONTATO = 6;
const TETO_SOLICITACAO = 5;
const TETO_ACAO = 4;
const TETO_LIDERANCA = 4;

interface ItemPaleta {
  chave: string;
  /** Categoria, como o `k` do protótipo. */
  k: string;
  /** Título. */
  t: string;
  /** Subtítulo — é o que distingue dois homônimos (proto:563-570). */
  s: string;
  ir: () => void;
}

export function CommandPalette() {
  const navigate = useNavigate();
  const { isOpen, query, setQuery, close } = useCommandPalette();
  const token = useAuthStore((s) => s.token) || '';
  const setBairroActiveId = useMockStore((s) => s.setBairroActiveId);
  const { bairros } = useBairros();
  const [realce, setRealce] = useState(0);

  // Ctrl+K global
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        useCommandPalette.getState().toggle();
      }
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [close]);

  const bruto = query.trim();
  const q = bruto.toLowerCase();

  // Telas e territórios são locais (ROTAS é constante; os territórios já estão
  // no cache compartilhado de `useBairros`), então filtram na hora. Só as
  // quatro categorias que batem no servidor passam pelo debounce.
  //
  // O termo vai ao servidor como foi digitado: é o servidor que decide como
  // comparar caixa, e o protocolo da solicitação é gravado em caixa alta.
  const [termo, setTermo] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTermo(bruto), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [bruto]);

  const busca = termo.length >= MIN_BUSCA ? termo : '';
  const podeBuscar = isOpen && !!token && !!busca;

  const { data: contatos } = useQuery({
    queryKey: ['paleta', 'contatos', busca],
    queryFn: () => contatosApi.list({ search: busca, limit: TETO_CONTATO }, token),
    enabled: podeBuscar,
  });

  const { data: solicitacoes } = useQuery({
    queryKey: ['paleta', 'solicitacoes', busca],
    queryFn: () => solicitacoesApi.list({ search: busca }, token),
    enabled: podeBuscar,
  });

  const { data: acoes } = useQuery({
    queryKey: ['paleta', 'acoes', busca],
    queryFn: () => acoesApi.list({ search: busca }, token),
    enabled: podeBuscar,
  });

  const { data: liderancas } = useQuery({
    queryKey: ['paleta', 'liderancas', busca],
    queryFn: () => liderancasApi.list({ search: busca }, token),
    enabled: podeBuscar,
  });

  const go = (caminho: string) => {
    navigate(caminho);
    close();
    setQuery('');
  };

  const itens: ItemPaleta[] = useMemo(() => {
    const lista: ItemPaleta[] = [];

    // Telas: fonte única da Fase 1 (`lib/navegacao.ts`). Sem busca, entram
    // todas — como no protótipo. `noMenu` fica de fora porque o protótipo já
    // tinha removido essas rotas do `ROUTES` quando monta a paleta.
    for (const r of ROTAS) {
      if (r.noMenu) continue;
      if (q && !r.nome.toLowerCase().includes(q)) continue;
      lista.push({
        chave: `tela:${r.caminho}`,
        k: 'Tela',
        t: r.nome,
        s: grupoDaRota(r.caminho),
        ir: () => go(r.caminho),
      });
    }

    if (q) {
      // Deep-link: mesma convenção da Task 5.2 (`/contatos?contato=<id>`),
      // estendida por tipo — `?solicitacao=`, `?acao=`, `?lideranca=`.
      // `?emenda=` já existia em `EmendasPage.tsx`.
      for (const c of (contatos?.items ?? []).slice(0, TETO_CONTATO)) {
        lista.push({
          chave: `contato:${c.id}`,
          k: 'Contato',
          t: c.nome,
          s: c.bairro || '—',
          ir: () => go(`/contatos?contato=${encodeURIComponent(c.id)}`),
        });
      }

      for (const s of (solicitacoes ?? []).slice(0, TETO_SOLICITACAO)) {
        lista.push({
          chave: `solicitacao:${s.id}`,
          k: 'Solicitação',
          t: s.tema,
          s: s.status,
          ir: () => go(`/solicitacoes?solicitacao=${encodeURIComponent(s.id)}`),
        });
      }

      for (const a of (acoes ?? []).slice(0, TETO_ACAO)) {
        lista.push({
          chave: `acao:${a.id}`,
          k: 'Ação',
          t: a.titulo,
          s: a.status,
          ir: () => go(`/acoes?acao=${encodeURIComponent(a.id)}`),
        });
      }

      for (const l of (liderancas ?? []).slice(0, TETO_LIDERANCA)) {
        lista.push({
          chave: `lideranca:${l.id}`,
          k: 'Liderança',
          t: l.nome,
          s: `${l.bairro || '—'} · saúde ${l.saude ?? '—'}`,
          ir: () => go(`/liderancas?lideranca=${encodeURIComponent(l.id)}`),
        });
      }

      // Território não tem teto próprio no protótipo. Também não tem
      // deep-link por query: a convenção que já existe (`LiderancasPage.tsx`,
      // `MetasPage.tsx`) é selecionar o território ativo no store e navegar —
      // é o que o protótipo faz (`STATE.bairro=b.id;go('terr')`).
      for (const b of bairros.filter((x) => x.n.toLowerCase().includes(q))) {
        lista.push({
          chave: `terr:${b.id}`,
          k: 'Território',
          t: b.n,
          s: `${b.pop.toLocaleString('pt-BR')} moradores`,
          ir: () => {
            setBairroActiveId(b.id);
            go('/territorio');
          },
        });
      }
    }

    return lista.slice(0, TETO_GLOBAL);
    // `go`/`setBairroActiveId` são estáveis o bastante; recalcular a lista a
    // cada render de rota só trocaria as referências dos callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, contatos, solicitacoes, acoes, liderancas, bairros]);

  useEffect(() => {
    setRealce(0);
  }, [q, isOpen]);

  if (!isOpen) return null;

  const mover = (passo: number) => {
    if (itens.length === 0) return;
    setRealce((i) => (i + passo + itens.length) % itens.length);
  };

  const teclado = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      mover(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      mover(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      itens[realce]?.ir();
    }
  };

  return (
    <div
      className="pal open"
      role="dialog"
      aria-modal="true"
      aria-label="Paleta de comandos"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="box" role="combobox" aria-expanded="true" aria-haspopup="listbox">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={teclado}
          // O placeholder do protótipo promete emendas (proto:356). Aqui ele
          // lista só o que a busca realmente alcança — ver o cabeçalho.
          placeholder="Buscar em telas, contatos, solicitações, ações, lideranças e territórios…"
          aria-label="Busca de comandos"
          aria-autocomplete="list"
          aria-controls="cmd-results"
          aria-activedescendant={itens[realce] ? `cmd-${realce}` : undefined}
        />
        <div className="res" id="cmd-results" role="listbox">
          {itens.map((item, i) => (
            <div
              key={item.chave}
              id={`cmd-${i}`}
              className={`it ${i === realce ? 'sel' : ''}`}
              role="option"
              aria-selected={i === realce ? 'true' : 'false'}
              onClick={() => item.ir()}
              onMouseEnter={() => setRealce(i)}
            >
              <span className="k">{item.k}</span>
              <span>{item.t}</span>
              <span className="s">{item.s}</span>
            </div>
          ))}
          {itens.length === 0 && <div className="empty">Nada encontrado.</div>}
        </div>
        <div className="pal-nota">
          Emendas e Agenda interna não são buscadas: as APIs dessas duas telas não aceitam busca
          por texto no servidor.
        </div>
      </div>
    </div>
  );
}
